import { beatglide } from './beatglide.js';

/**
 * File: src/data/games.js
 * Mô tả: Danh mục dữ liệu toàn bộ các tựa game được phát triển bởi SNT Games, bao gồm tựa game chính Ant Clash.
 * Liên quan: src/components/hero.js, src/components/games-showcase.js, src/components/game-detail-modal.js
 */

/**
 * @typedef {Object} DocumentLink
 * @property {string} title - Tên tài liệu (VD: Press Kit, Game Manual)
 * @property {string} url - Đường dẫn tải tài liệu
 * @property {string} type - Loại tài liệu (PDF, ZIP, MD)
 */

/**
 * @typedef {Object} PlatformLink
 * @property {string} name - Nền tảng (Steam, App Store, Google Play, Web)
 * @property {string} url - Link tải/chơi game
 * @property {boolean} available - Trạng thái đã phát hành hay chưa
 */

/**
 * @typedef {Object} Game
 * @property {string} id - Định danh duy nhất
 * @property {string} title - Tên trò chơi
 * @property {string} genre - Thể loại game
 * @property {string} shortDescription - Mô tả ngắn hiển thị trên thẻ
 * @property {string} fullDescription - Mô tả chi tiết hiển thị trong Modal
 * @property {string} icon - Ảnh biểu tượng icon ứng dụng (512x512)
 * @property {string} coverImage - Ảnh bìa đại diện (Poster)
 * @property {string[]} screenshots - Danh sách đường dẫn ảnh màn hình
 * @property {string} trailerUrl - URL video trailer (MP4 hoặc Embed)
 * @property {string} [trailerPoster] - Ảnh poster hiển thị trước khi phát trailer MP4
 * @property {DocumentLink[]} documents - Tài liệu & Press Kit liên quan
 * @property {PlatformLink[]} platformLinks - Liên kết tải game trên các nền tảng
 * @property {boolean} featured - Có phải game nổi bật hiển thị ở Hero không
 * @property {string} releaseDate - Ngày phát hành hoặc dự kiến
 * @property {string} rating - Đánh giá hoặc độ tuổi (VD: PEGI 7, 4.9★)
 */

/** @type {Game[]} */
export const games = [
  beatglide,
  {
    id: 'ant-clash',
    title: 'Ant Clash',
    genre: '2D Real-Time Strategy',
    shortDescription: 'Game chiến thuật thời gian thực 2D nơi bạn chỉ huy đàn kiến đối đầu với đàn mối để chiếm quyền kiểm soát các tổ.',
    fullDescription: 'Ant Clash là tựa game chiến thuật thời gian thực 2D nhịp độ nhanh. Người chơi chỉ huy thuộc địa kiến mở rộng qua 5 chiến dịch (Vườn sương sớm, Hang rễ cổ thụ, Bờ suối đá cuội, Cồn cát hoàng hôn và Pháo đài Kiến Chúa). Game sở hữu 12 loại kiến với chỉ số sức mạnh, máu và tốc độ khác nhau, cùng 3 kỹ năng chủ động tối thượng: Hỏa kích, Cuồng nộ sinh sản và Pheromone cuồng chiến.',
    icon: '/assets/images/ant-clash/app_icon_512.jpg',
    coverImage: '/assets/images/ant-clash/game1.png',
    screenshots: [
      '/assets/images/ant-clash/game1.png',
      '/assets/images/ant-clash/game2.png',
      '/assets/images/ant-clash/game3.png',
      '/assets/images/ant-clash/selectmap.png',
      '/assets/images/ant-clash/upgrade.png',
      '/assets/images/ant-clash/win.png',
    ],
    trailerUrl: '/assets/videos/ant-clash/AntClash.mp4',
    documents: [
      { title: 'Giới Thiệu Tổng Quan Ant Clash', url: '/assets/documents/ant-clash/GIOI_THIEU_ANT_CLASH.md', type: 'Markdown / Doc' },
      { title: 'Hướng Dẫn Người Chơi & Chiến Thuật', url: '/assets/documents/ant-clash/HUONG_DAN_CHO_NGUOI_CHOI.md', type: 'Markdown / Doc' },
      { title: 'Hệ Thống Tiến Trình & Nâng Cấp', url: '/assets/documents/ant-clash/HE_THONG_VA_TIEN_TRINH.md', type: 'Markdown / Doc' },
      { title: 'Tham Chiếu Kỹ Thuật & Chỉ Số', url: '/assets/documents/ant-clash/THAM_CHIEU_KY_THUAT.md', type: 'Markdown / Doc' },
    ],
    platformLinks: [
      { name: 'Google Play', url: 'https://play.google.com/store/apps/details?id=com.sntgames.antclash', available: true },
    ],
    featured: true,
    releaseDate: 'Chính thức phát hành',
    releaseDateEn: 'Released',
    platforms: 'Android',
    gameplay: 'Điều quân theo các tuyến đường, chiếm tổ, nâng cấp đàn kiến và dùng ba kỹ năng để vượt qua 55 màn chiến dịch.',
    gameplayEn: 'Send troops along routes, capture nests, upgrade ants and use three skills across 55 campaign levels.',
  },
  {
    id: 'xiangqi',
    title: 'Xiangqi',
    genre: 'Cờ tướng',
    genreEn: 'Chinese Chess',
    shortDescription: 'Cờ tướng đã hoàn thành, sẵn sàng để khám phá tại xiangqionline.com.',
    shortDescriptionEn: 'A completed Chinese chess game, ready to explore at xiangqionline.com.',
    fullDescription: 'Xiangqi là sản phẩm cờ tướng của SNT Games đã hoàn thành. Khám phá thế giới cờ tướng và trải nghiệm trò chơi tại xiangqionline.com. Bộ ảnh bên dưới giới thiệu các màn hình và chế độ của sản phẩm.',
    fullDescriptionEn: 'Xiangqi is a completed Chinese chess game by SNT Games. Explore the world of Chinese chess at xiangqionline.com. The gallery below introduces the game’s screens and modes.',
    coverImage: '/assets/images/xiangqi/online.png',
    screenshots: ['online.png', 'AI.png', 'Offline.png', 'Puzzle.png', 'Analyze.png', 'book.png', 'Hiden.png', 'social.png'].map((name) => `/assets/images/xiangqi/${name}`),
    trailerUrl: '',
    documents: [],
    platformLinks: [
      { name: 'Google Play', url: 'https://play.google.com/store/apps/details?id=com.xiangqionline.app', available: true },
      { name: 'xiangqionline.com', url: 'https://xiangqionline.com', available: true },
    ],
    featured: false,
    releaseDate: 'Đã phát hành',
    releaseDateEn: 'Released',
    platforms: 'Android / Web',
    gameplay: 'Chơi cờ tướng trực tuyến, luyện với AI, giải thế cờ và phân tích ván đấu. Tên trên Google Play: Ky Vuong: Ultimate Xiangqi.',
    gameplayEn: 'Play Xiangqi online, practice against AI, solve puzzles and analyze matches. Google Play title: Ky Vuong: Ultimate Xiangqi.',
  },
  {
    id: 'zombieday',
    title: 'ZombieDay',
    genre: 'Game chủ đề zombie',
    genreEn: 'Zombie Game',
    shortDescription: 'Dự án game chủ đề zombie đang được SNT Games phát triển. Xem trước hình ảnh đầu tiên của sản phẩm.',
    shortDescriptionEn: 'A zombie-themed game currently in development at SNT Games. Discover an early preview of the project.',
    fullDescription: 'ZombieDay là dự án game chủ đề zombie đang trong quá trình phát triển tại SNT Games. Hình ảnh dưới đây giới thiệu giao diện hiện tại của dự án; nội dung có thể thay đổi trong quá trình phát triển. Thông tin phát hành sẽ được cập nhật khi sản phẩm sẵn sàng.',
    fullDescriptionEn: 'ZombieDay is a zombie-themed game currently in development at SNT Games. The images below preview its current interface and may change during development. Release information will be shared when the game is ready.',
    coverImage: '/assets/images/zombieday/home.png',
    screenshots: ['/assets/images/zombieday/home.png'],
    trailerUrl: '',
    documents: [],
    platformLinks: [],
    featured: false,
    releaseDate: 'Đang phát triển',
    releaseDateEn: 'In development — Coming soon',
    platforms: 'Chưa công bố / To be announced',
    gameplay: 'Gameplay và nền tảng sẽ được công bố khi dự án sẵn sàng. Chưa có bản tải công khai.',
    gameplayEn: 'Gameplay and platforms will be announced when ready. No public download is available yet.',
  },
];
